import { memo } from "react";
import { useAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { userInformationAtom } from "~/stores/UserStore";
import { Avatar as AntdAvatar, Button } from "antd";
import { getHashColor } from "~/utils/hashStringUtil";
import Tippy from "@tippyjs/react/headless";
import styled from "styled-components";
import { API_URL_ADMIN } from "~/config/config";
import Routes from "~/routes/routes";
import { PiUserBold, PiGearBold, PiSignOutBold, PiSignInBold } from "react-icons/pi";

interface AvatarProps {
  showName?: boolean;
}

const TippyWrapper = (props: any) => {
  const { children, userInfo } = props;
  const navigate = useNavigate();

  return (
    <Tippy
      trigger="click"
      interactive={true}
      placement="bottom-end"
      offset={[0, 8]}
      render={(attrs) => {
        return (
          <UI_Options_Wrapper {...attrs}>
            {userInfo && (
              <UI_User_Header>
                <div className="name">{userInfo.fullname || userInfo.username}</div>
                {userInfo.email && <div className="email">{userInfo.email}</div>}
              </UI_User_Header>
            )}
            <UI_Divider />
            <UI_Options_Button
              onClick={() => {
                navigate(Routes.PROFILE);
              }}
            >
              <PiUserBold style={{ fontSize: 16 }} />
              <span>Thông tin cá nhân</span>
            </UI_Options_Button>
            <UI_Options_Button
              onClick={() => {
                navigate(Routes.SETTING);
              }}
            >
              <PiGearBold style={{ fontSize: 16 }} />
              <span>Cài đặt</span>
            </UI_Options_Button>
            <UI_Divider />
            <UI_Options_Button
              className="danger"
              onClick={() => {
                localStorage.removeItem('jwt');
                window.location.reload();
              }}
            >
              <PiSignOutBold style={{ fontSize: 16 }} />
              <span>Đăng xuất</span>
            </UI_Options_Button>
          </UI_Options_Wrapper>
        );
      }}
    >
      {children}
    </Tippy>
  );
};

const Avatar: React.FC<AvatarProps> = ({ showName = false }) => {
  const [userInformation] = useAtom(userInformationAtom);
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!userInformation) {
    const handleLogin = () => {
      const currentUrl = window.location.pathname + window.location.search;
      navigate(`${Routes.LOGIN}?goto=${encodeURIComponent(currentUrl)}`);
    };

    return (
      <UI_LoginButton type="primary" onClick={handleLogin}>
        <PiSignInBold style={{ fontSize: 16 }} />
        <span>{t('auth.login.name') || 'Đăng nhập'}</span>
      </UI_LoginButton>
    );
  }
  const { backgroundColor, foregroundColor } = { ...getHashColor(userInformation.username) };

  const avatarElement = userInformation.avatar ? (
    <AntdAvatar
      size={28}
      src={`${API_URL_ADMIN}${(userInformation.avatar as any).url}`}
      style={{ userSelect: 'none', cursor: 'pointer' }}
    />
  ) : (
    <AntdAvatar
      size={28}
      style={{
        backgroundColor: backgroundColor,
        color: foregroundColor,
        userSelect: 'none',
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {(userInformation.fullname || userInformation.username).slice(0, 2).toUpperCase()}
    </AntdAvatar>
  );

  return (
    <TippyWrapper userInfo={userInformation}>
      <UI_AvatarTrigger>
        {avatarElement}
        {showName && (
          <UI_UserName title={userInformation.fullname || userInformation.username}>
            {userInformation.fullname || userInformation.username}
          </UI_UserName>
        )}
      </UI_AvatarTrigger>
    </TippyWrapper>
  );
};

export default memo(Avatar);

const UI_AvatarTrigger = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 3px 6px;
  border-radius: 6px;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(59, 130, 246, 0.08);
  }
`;

const UI_UserName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 640px) {
    display: none;
  }
`;

const UI_Options_Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #f1f5f9;
  padding: 6px 0;
  min-width: 180px;
  z-index: 2500;
`;

const UI_User_Header = styled.div`
  padding: 8px 14px 6px;
  
  .name {
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .email {
    font-size: 11px;
    color: #64748b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-top: 2px;
  }
`;

const UI_Divider = styled.div`
  height: 1px;
  background: #f1f5f9;
  margin: 4px 0;
`;

const UI_Options_Button = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 13px;
  color: #475569;

  &:hover {
    background-color: rgba(59, 130, 246, 0.08);
    color: var(--color-primary);
  }

  &.danger {
    color: #ef4444;

    &:hover {
      background-color: #fef2f2;
      color: #dc2626;
    }
  }
`;

const UI_LoginButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 14px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 6px;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  border: none;
  box-shadow: 0 1px 3px rgba(37, 99, 235, 0.3);
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%) !important;
    color: #ffffff !important;
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(37, 99, 235, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 480px) {
    padding: 0 10px;
    font-size: 12px;
  }
`;